import { bool, boolean, mixed, object, string } from "yup";
import { dayjs } from "@thalamos/common";
export const defaultAddress = {
    address: "",
    postalCode: "",
    isConfirmed: false,
    name: "",
};
export function cleanStrangeWhitespaceFromText(value) {
    if (value === null) {
        return null;
    }
    // 00A0 - no break space
    // 1680 - ogham space mark
    // 180E - mongolian vowel separator
    // 2000-200B - various space characters
    // 202F - narrow no-break space
    // 205F - medium mathematical space
    // 3000 - ideographic space
    // FEFF - zero-width no-break space
    // 0000-0003 - control characters
    // 000C - form feed
    return (value
        .replaceAll(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, " ")
        // eslint-disable-next-line no-control-regex
        .replaceAll(/[\u0000-\u0003\u000c]/g, ""));
}
export function dateAndTimeWithin24HoursValidation() {
    return isoDateTimeString.test("within-24-hours", "You cannot enter a date and/or time more than 24 hours in the past.", (value) => {
        return dateAndTimeWithin24Hours(value);
    });
}
export function dateAndTimeWithin24Hours(value) {
    const currentDate = dayjs();
    const twentyFourHoursAgo = currentDate.subtract(24, "hour");
    return dayjs(value).isAfter(twentyFourHoursAgo);
}
// Common function for date validation
function generateDateValidationRule(dateType, description, validationFunction) {
    const formatString = dateType === "date-time" ? isoDateTimeString : isoDateString;
    return formatString.test("date-range-validation", description, validationFunction);
}
// Function to validate if a date is before today
export function generateDateBeforeTodayValidationRule(dateType, description) {
    return generateDateValidationRule(dateType, description, isDateBeforeToday);
}
export function generateDateBeforeNowValidationRule(dateType, description) {
    return generateDateValidationRule(dateType, description, isDateBeforeNow);
}
// Function to validate if a date is after today
export function generateDateAfterTodayValidationRule(dateType, description) {
    return generateDateValidationRule(dateType, description, isDateAfterToday);
}
export function generateDateAfterNowValidationRule(dateType, description) {
    return generateDateValidationRule(dateType, description, isDateAfterNow);
}
// Returns the current date in the UK timezone as a YYYY-MM-DD string.
// Using .tz("Europe/London") is correct regardless of the runtime's local
// timezone (UTC on the server, GMT/BST on a UK browser), because dayjs always
// converts the absolute instant to the named zone rather than relying on
// process.env.TZ.
function todayInUk() {
    return dayjs().tz("Europe/London").format("YYYY-MM-DD");
}
function isDateBeforeNow(value) {
    if (!value) {
        // Allow null values
        return true;
    }
    const currentDate = dayjs();
    const enteredDate = dayjs(value);
    return enteredDate.isSameOrBefore(currentDate, "minute");
}
function isDateAfterNow(value) {
    if (!value) {
        // Allow null values
        return true;
    }
    const currentDate = dayjs();
    const enteredDate = dayjs(value);
    return enteredDate.isSameOrAfter(currentDate, "minute");
}
// Shared function to check if a given date is before today
function isDateBeforeToday(value) {
    if (!value) {
        // Allow null values
        return true;
    }
    // Compare the first 10 characters (YYYY-MM-DD) of the value against today's
    // UK date. ISO date strings are lexicographically sortable, so string
    // comparison is unambiguous. Using value.slice(0, 10) also handles ISO
    // datetime strings correctly when the Today validators are used with
    // dateType "date-time".
    return value.slice(0, 10) <= todayInUk();
}
// Shared function to check if a given date is after today
function isDateAfterToday(value) {
    if (!value) {
        // Allow null values
        return true;
    }
    return value.slice(0, 10) >= todayInUk();
}
export function postalCodeValidationRule(propertyName, valueToMatch) {
    return string()
        .test("postalCodeRequired", "Postal code is required", function (value, context) {
        // Retrieve the value of the specified property name from the context
        const propertyValue = context?.from?.[1]?.value[propertyName];
        // Check if the property value matches the provided value and postal code is not provided
        if (propertyValue === valueToMatch && !value) {
            // Return a validation error indicating that the postal code is required
            return this.createError({
                message: "Postal code is required",
                path: "address.postalCode",
            });
        }
        return true;
    })
        .default("")
        .max(8, formValidationErrorMessages.postalCodeCharacterError);
}
export function requiredPostalCode() {
    return string()
        .trim()
        .required(formValidationErrorMessages.postalCodeRequiredError)
        .max(8, formValidationErrorMessages.postalCodeCharacterError);
}
export const optionalAddress = () => {
    return object({
        address: string()
            .trim()
            .max(255, formValidationErrorMessages.tooManyCharactersError),
        postalCode: string()
            .trim()
            .max(8, formValidationErrorMessages.postalCodeCharacterError), // TODO: regex
        isConfirmed: bool()
            .default(false)
            .when("address", {
            is: (a) => a,
            then: (s) => s.oneOf([true], "Please confirm the address"),
            otherwise: (s) => s,
        }),
    })
        .nullable()
        .default(defaultAddress);
};
export const formValidationErrorMessages = {
    nameRequiredError: "Name is required",
    addressRequiredError: "Address is required",
    addressConfirmationRequiredError: "Address confirmation is required",
    postalCodeRequiredError: "Please enter a postal code",
    postalCodeCharacterError: "Too many characters entered for postcode.",
    tooManyCharactersError: "Too many characters entered.",
};
export const nameAndAddress = (errorOverrides = {}, isPostCodeRequired = false) => {
    const errorMessages = {
        ...formValidationErrorMessages,
        ...errorOverrides,
    };
    return object({
        name: string()
            .trim()
            .required(errorMessages.nameRequiredError)
            .max(255, errorMessages.tooManyCharactersError),
        address: string()
            .trim()
            .required(errorMessages.addressRequiredError)
            .max(255, errorMessages.tooManyCharactersError),
        postalCode: isPostCodeRequired
            ? requiredPostalCode()
            : string().trim().max(8, errorMessages.postalCodeCharacterError),
        isConfirmed: bool()
            .default(false)
            .when("address", {
            is: (a) => a,
            then: (s) => s.oneOf([true], errorMessages.addressConfirmationRequiredError),
            otherwise: (s) => s,
        }),
    })
        .nullable()
        .default(null);
};
export const hospital = object({
    name: string()
        .trim()
        .required("Please enter hospital name")
        .max(255, formValidationErrorMessages.tooManyCharactersError),
    address: string()
        .trim()
        .required("Please enter hospital address")
        .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: requiredPostalCode(), // TODO regex
    isConfirmed: bool()
        .required("Please confirm the name and address is correct")
        .oneOf([true], "Please confirm the name and address is correct"),
    // thalamos internal ID fields - both optional if manually entered (for now)
    hospitalId: string().uuid().optional(),
    trustId: string().uuid().optional(),
    trustName: string().optional(),
    trustOdsCode: string().optional(),
})
    .required("hospital-required")
    .default(defaultAddress);
/**
 * Place of Safety schema - can be either a curated PoS (with id) or ad-hoc (manually entered)
 */
export const placeOfSafety = object({
    name: string()
        .trim()
        .required("Please enter Place of Safety name")
        .max(255, formValidationErrorMessages.tooManyCharactersError),
    address: string()
        .trim()
        .required("Please enter Place of Safety address")
        .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: requiredPostalCode(),
    isConfirmed: bool()
        .required("Please confirm the name and address is correct")
        .oneOf([true], "Please confirm the name and address is correct"),
    // Curated PoS fields - optional for ad-hoc entries
    placeOfSafetyId: string().uuid().optional(),
    trustId: string().uuid().optional(),
    trustName: string().optional(),
})
    .required("place-of-safety-required")
    .default(defaultAddress);
export const practitioner = ({ localAuthorityRequired, section12Required, approvedClinicianRequired, userAddressOptional, userNameOptional, nameErrorMessage, } = {}) => object({
    // Thalamos user ID
    id: string(),
    email: string()
        .trim()
        .matches(/.+@.+\..+/)
        .required("Email address is required"),
    name: userNameOptional
        ? string()
            .nullable()
            .optional()
            .max(255, formValidationErrorMessages.tooManyCharactersError)
        : string()
            .trim()
            .required(nameErrorMessage || formValidationErrorMessages.nameRequiredError)
            .max(255, formValidationErrorMessages.tooManyCharactersError),
    address: userAddressOptional
        ? string()
            .nullable()
            .optional()
            .max(255, formValidationErrorMessages.tooManyCharactersError)
        : string()
            .trim()
            .required(formValidationErrorMessages.addressRequiredError)
            .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: userAddressOptional
        ? string()
            .trim()
            .max(8, formValidationErrorMessages.postalCodeCharacterError)
        : requiredPostalCode(), // TODO regex
    isConfirmed: userAddressOptional
        ? bool()
        : bool().required().oneOf([true], "Please confirm your address"),
    isGuest: bool().required(),
    isSection12Approved: section12Required
        ? boolean().required("Please confirm your Section 12 approval status")
        : boolean().nullable().optional(),
    isApprovedClinician: approvedClinicianRequired
        ? boolean().oneOf([true], "Please confirm your Approved/Responsible Clinician status")
        : boolean().nullable().optional(),
    localAuthority: localAuthorityRequired
        ? string()
            .trim()
            .required("Please enter the Local Authority that approved you")
        : string().optional(),
}).default({ ...defaultAddress, email: "", name: "" });
export const isTimeString = string()
    .matches(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "Please enter a valid time")
    .nullable()
    .default(null);
export const isoDateString = string()
    .matches(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/, "Please enter a valid date")
    .nullable()
    .default(null);
export const isoDateTimeString = string()
    .matches(/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/, "Please enter a valid date / time")
    .nullable()
    .default(null);
export const nonEmptyString = string()
    .transform(cleanStrangeWhitespaceFromText)
    .trim()
    .nullable()
    .default(null);
export const nonEmptyRequiredString = nonEmptyString.required().nonNullable();
export const defaultAmhpSchemaFields = {
    amActingOnBehalfOfApprovingAuthority: boolean()
        .default(null)
        .required("Please select the option which applies"),
    nameOfLocalAuthorityActingOnBehalfOf: nonEmptyString.when("amActingOnBehalfOfApprovingAuthority", {
        is: false,
        then: (schema) => schema.required("Please enter the local social services authority you are acting on behalf of"),
        otherwise: (schema) => schema.notRequired(),
    }),
};
export const defaultContextFields = (opts = {}) => ({
    user: practitioner(opts).required(),
    patient: object({
        name: string()
            .trim()
            .required(formValidationErrorMessages.nameRequiredError)
            .max(255, formValidationErrorMessages.tooManyCharactersError),
        address: string()
            .trim()
            .required()
            .max(255, formValidationErrorMessages.tooManyCharactersError),
        postalCode: string().trim(), // intentionally not setting postcode to max 8, further details as to why, in this tickets comments https://thalamos.atlassian.net/browse/ASP-2124 // TODO: regex
        dateOfBirth: isoDateString.nullable().optional(),
        // kept in for back compatibility - can be deleted a few months post Jan 2025
        nhsNumber: mixed().nullable().optional(),
    })
        .default({
        postalCode: "",
        address: "",
        name: "",
        dateOfBirth: null,
        nhsNumber: null,
    })
        .required(),
});
export const confirmCheckbox = boolean()
    .oneOf([true], "Please confirm statement")
    .default(false)
    .required("Please confirm statement");
export const optionalCheckbox = boolean().default(false).required();
//# sourceMappingURL=shared.js.map